"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import AuthorPreview from "@/components/pages/blog/post/AuthorPreview";
import Link from "next/link";
import { useRouter } from "next/navigation";
import clsx from "clsx";
import { type MarkdownHeading } from "@/lib/utils/markdown-headings";

type SectionLink = {
  key: string;
  text: string;
  style: string;
  additionalClasses?: string;
};

function mapHeadingsToSectionLinks(headings: MarkdownHeading[]): SectionLink[] {
  return headings.map(
    (heading): SectionLink => ({
      key: heading.id,
      text: heading.text,
      style: heading.style,
      additionalClasses:
        heading.level <= 2 ? "py-2" : "border-l-2 border-grey-850 pl-4 py-2",
    }),
  );
}

export default function SideBar({
  className,
  authors,
  coAuthors,
  title,
  blog,
  bannerActive,
  markdownHeadings = [],
}: {
  className?: string;
  authors?: Author[];
  coAuthors?: Author[];
  title?: string;
  blog?: boolean;
  bannerActive?: boolean;
  markdownHeadings?: MarkdownHeading[];
}) {
  const [scrolling, setScrolling] = useState(false);
  const [activeSection, setActiveSection] = useState<{
    key: string;
    heading: "h1" | "h2" | "h3" | "h4";
  }>();
  const sticky = useRef<HTMLDivElement>(null);
  const sidebarWrapper = useRef<HTMLDivElement>(null);
  const router = useRouter();

  // Handle initial anchor scroll and set active section
  useEffect(() => {
    const anchor = window.location.hash.replace("#", "");
    if (!anchor) return;
    setScrolling(true);

    const element = document.getElementById(anchor);
    if (element) {
      const heading = element.tagName.toLowerCase() as
        | "h1"
        | "h2"
        | "h3"
        | "h4";
      setActiveSection({
        key: anchor,
        heading,
      });

      setTimeout(() => {
        element.scrollIntoView({ behavior: "smooth" });
        setScrolling(false);
      }, 100);
    }
  }, []);

  // Keep active link in viewport within scrollable sidebar
  useEffect(() => {
    if (!activeSection?.key || scrolling || !sidebarWrapper.current) return;

    const activeLink = document.querySelector(
      `#sidebar-list a[href="#${activeSection.key}"]`,
    ) as HTMLElement;

    if (activeLink) {
      const wrapperHeight = sidebarWrapper.current.clientHeight;
      const linkTop = activeLink.offsetTop - sidebarWrapper.current.scrollTop;

      if (linkTop < 100 || linkTop > wrapperHeight - 100) {
        sidebarWrapper.current.scrollTo({
          top: activeLink.offsetTop - wrapperHeight / 2,
          behavior: "smooth",
        });
      }
    }
  }, [activeSection?.key, scrolling]);

  // Initialize section links
  const sectionLinks = useMemo(() => {
    const markdownLinks =
      markdownHeadings.length > 0
        ? mapHeadingsToSectionLinks(markdownHeadings)
        : [];

    const newSectionLinks = blog
      ? [
          {
            key: "post-header",
            text: "Intro",
            style: "h1",
            additionalClasses: "py-2",
          },
          ...markdownLinks,
        ]
      : markdownLinks;

    return newSectionLinks;
  }, [blog, markdownHeadings]);

  useEffect(() => {
    // Set initial active section if none exists
    if (!activeSection && sectionLinks.length > 0) {
      const initialSection = {
        key: blog ? "post-header" : sectionLinks[0].key,
        heading: blog
          ? "h1"
          : (sectionLinks[0].style as "h1" | "h2" | "h3" | "h4"),
      };
      setActiveSection(initialSection);
    }
  }, [activeSection, blog, sectionLinks]);

  // Intersection observer for tracking visible sections
  useEffect(() => {
    if (!sectionLinks.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (scrolling) return;

        const visibleEntries = entries.filter((entry) => entry.isIntersecting);
        if (visibleEntries.length === 0) return;

        // Find the topmost visible section
        const topEntry = visibleEntries.reduce((prev, curr) => {
          return prev.boundingClientRect.top > curr.boundingClientRect.top
            ? curr
            : prev;
        });

        const matchingLink = sectionLinks.find(
          (link) => link.key === topEntry.target.id,
        );

        if (matchingLink) {
          setActiveSection({
            key: matchingLink.key,
            heading: topEntry.target.tagName.toLowerCase() as
              | "h1"
              | "h2"
              | "h3"
              | "h4",
          });
        }
      },
      {
        root: null,
        rootMargin: "0px 0px -70% 0px",
        threshold: 0.5,
      },
    );

    sectionLinks.forEach((link) => {
      const element = document.getElementById(link.key);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [scrolling, sectionLinks]);


  if (!authors?.length && !sectionLinks.length) {
    return null;
  }

  return (
    <div
      className={clsx(
        "sticky flex flex-col left-0 sidebar-container h-[calc(100vh-60px)]!",
        blog
          ? bannerActive
            ? "top-16"
            : "top-7"
          : bannerActive
            ? "top-32"
            : "top-24",
        className,
      )}
      ref={sticky}
    >
      <div className={clsx("w-full flex-1 relative", !blog && "mt-1")}>
        <div className="bg-linear-to-t from-transparent to-25% to-grey-950 sticky left-0 right-0 top-0 z-10 h-24 -mt-32 hidden md:block" />
        {sectionLinks && sectionLinks.length > 1 && (
          <div
            className={clsx(
              "overflow-y-auto h-full pb-16 leading-none hide-scrollbar",
              title && "pt-8 mt-0",
            )}
            ref={sidebarWrapper}
            style={{
              height:
                authors && authors.length > 0 ? `calc(100vh - 10rem)` : "90vh",
            }}
          >
            {title && <span className="blog-tag pb-2 pl-3">{title}</span>}
            <ul
              id="sidebar-list"
              className={clsx(
                "hidden lg:flex flex-col relative pl-4",
                blog ? "pt-16" : "pt-4",
              )}
            >
              {sectionLinks.map((link, index) => {
                const isActive = activeSection?.key === link.key;
                return (
                  <Link
                    key={`${link.key}-${index}`}
                    onClick={(e) => {
                      e.preventDefault();
                      router.push("#" + link.key);
                    }}
                    href={"#" + link.key}
                    className={clsx(
                      "font-mono text-[9.5px] transition-colors leading-normal duration-100 w-full font-medium wrap-break-word relative",
                      isActive
                        ? "text-white"
                        : "text-neutral-400 hover:text-white",
                      link.additionalClasses,
                    )}
                  >
                    <div
                      id={isActive ? "highlighter" : undefined}
                      className={clsx(
                        "bg-red ring-[3px] ring-grey-950 h-2 w-2 aspect-square rounded-full absolute top-[15px] -translate-y-1/2",
                        "transition-all duration-150",
                        isActive ? "opacity-100 scale-100" : "opacity-0 scale-0",
                        link.style === "h1" || link.style === "h2"
                          ? "-left-4"
                          : "-left-4 ml-[11px]",
                      )}
                    />
                    {link.text}
                  </Link>
                );
              })}
            </ul>
          </div>
        )}
        {authors && authors.length > 1 && sectionLinks.length > 1 && (
          <div className="bg-linear-to-b from-transparent from-40% to-grey-950 sticky w-full bottom-0 z-10 h-24 -mt-24" />
        )}
      </div>
      {authors && authors.length > 0 ? (
        <div
          className={clsx(
            "left-0 pl-4 flex z-20 flex-col justify-end w-full sticky bottom-[35px] bg-linear-to-b from-transparent to-grey-950 to-20%",
            sectionLinks.length > 1 ? "pt-8 -mt-4" : "pt-16",
          )}
        >
          <span className="blog-tag block mb-2 font-medium">Authors</span>
          <div className="flex flex-col items-start">
            {authors.map((author, index) => (
              <AuthorPreview author={author} key={"author-" + index} />
            ))}
          </div>
          {coAuthors && coAuthors.filter(Boolean).length > 0 && (
            <>
              <span className="blog-tag block mt-3 mb-2 font-medium">Co-authors</span>
              <div className="flex flex-col items-start">
                {coAuthors.filter(Boolean).map((author, index) => (
                  <AuthorPreview author={author} key={"coauthor-" + index} />
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="absolute w-full h-24 bottom-0 z-10 bg-linear-to-b from-transparent to-grey-950 to-20%" />
        </>
      )}
    </div>
  );
}
